import { TooltipTriangle, TooltipTriangleHorizontal } from '@app/ui/iconComponents';
import { COLORS, TYPOGRAPHY } from '@b2b/shared/consts';
import {
  Side,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react-dom-interactions';
import { FC, ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import styled, { css, CSSProp } from 'styled-components';

interface Props {
  label?: string | ReactNode;
  placement?: Side;
  children: JSX.Element;
  className?: string;
  component?: ReactElement;
  isVisibleOnFocus?: boolean;
  isVisibleOnHover?: boolean;
  itemCSS?: CSSProp;
  triangleColor?: string;
}

const Tooltip: FC<Props> = ({
  children,
  label,
  placement = 'top',
  className,
  component,
  isVisibleOnHover = true,
  isVisibleOnFocus = true,
  itemCSS,
  triangleColor = COLORS.grayscale700,
}) => {
  const [isOpened, setIsOpened] = useState(false);
  const [triggerPosition, setTriggerPosition] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<DOMRect | null>(null);
  const [isPositionsCalculated, setIsPositionsCalculated] = useState(false);

  const {
    x,
    y,
    reference,
    floating,
    strategy,
    context,
    refs,
    update,
    placement: computedPlacement,
  } = useFloating({
    placement,
    open: isOpened,
    onOpenChange: setIsOpened,
    middleware: [offset(12), flip(), shift({ padding: 8 })],
  });

  const updatePosition = useCallback(() => {
    if (!refs.reference.current || !refs.floating.current) {
      return;
    }

    const triggerRect = refs.reference.current.getBoundingClientRect() as DOMRect;
    const tooltipRect = refs.floating.current.getBoundingClientRect() as DOMRect;

    setTriggerPosition(triggerRect);
    setTooltipPosition(tooltipRect);
    setIsPositionsCalculated(true);
  }, [refs]);

  useEffect(() => {
    if (isOpened) {
      requestAnimationFrame(() => {
        updatePosition();
      });
    } else {
      setIsPositionsCalculated(false);
    }
  }, [isOpened, updatePosition]);

  useEffect(() => {
    if (refs.reference.current && refs.floating.current && isOpened) {
      updatePosition();

      return autoUpdate(refs.reference.current, refs.floating.current, () => {
        update();
        updatePosition();
      });
    }
  }, [refs.reference, refs.floating, update, isOpened, updatePosition]);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { enabled: isVisibleOnHover }),
    useFocus(context, { enabled: isVisibleOnFocus }),
    useRole(context, { role: 'tooltip' }),
    useDismiss(context),
  ]);

  return (
    <div className={className}>
      <div {...getReferenceProps({ ref: reference, ...children.props })}>{children}</div>
      {isOpened && (
        <div
          {...getFloatingProps({
            ref: floating,
            style: {
              position: strategy,
              top: y ?? '',
              left: x ?? '',
            },
            onTransitionEnd: updatePosition,
          })}
        >
          {component ? (
            component
          ) : (
            <Item $CSS={itemCSS}>
              {label}
              {isPositionsCalculated && (
                <TriangleWrapper
                  $placement={computedPlacement as Side}
                  $triggerPosition={triggerPosition}
                  $tooltipPosition={tooltipPosition}
                >
                  {computedPlacement.includes('left') || computedPlacement.includes('right') ? (
                    <TooltipTriangleHorizontal color={triangleColor} />
                  ) : (
                    <TooltipTriangle color={triangleColor} />
                  )}
                </TriangleWrapper>
              )}
            </Item>
          )}
        </div>
      )}
    </div>
  );
};

export default Tooltip;

type DefaultCSSProp = { $CSS?: CSSProp };

interface StyleProps {
  $placement: Side;
  $triggerPosition: DOMRect | null;
  $tooltipPosition: DOMRect | null;
}

const Item = styled.div<DefaultCSSProp>`
  ${TYPOGRAPHY.body1Regular14}
  background-color: ${COLORS.grayscale700};
  border-radius: 8px;
  padding: 6px 8px;
  color: ${COLORS.white};
  pointer-events: none;
  z-index: 1;
  max-width: 250px;

  ${({ $CSS }) => $CSS}
`;

const TriangleWrapper = styled.div<StyleProps>`
  display: flex;
  position: absolute;

  ${({ $placement, $triggerPosition, $tooltipPosition }) => {
    if (!$triggerPosition || !$tooltipPosition) {
      if ($placement.includes('left') || $placement.includes('right')) {
        return css`
          top: 50%;
          transform: translateY(-50%);
        `;
      }
      if ($placement.includes('top') || $placement.includes('bottom')) {
        return css`
          left: 50%;
          transform: translateX(-50%);
        `;
      }
    }

    if ($placement.includes('left') || $placement.includes('right')) {
      if (!$triggerPosition || !$tooltipPosition) {
        return css`
          top: 50%;
          transform: translateY(-50%);
        `;
      }

      const triggerCenter = $triggerPosition.top + $triggerPosition.height / 2;
      const tooltipTop = $tooltipPosition.top;
      return css`
        top: ${triggerCenter - tooltipTop}px;
        transform: translateY(-50%);
      `;
    }

    if ($placement.includes('top') || $placement.includes('bottom')) {
      if (!$triggerPosition || !$tooltipPosition) {
        return css`
          left: 50%;
          transform: translateX(-50%);
        `;
      }

      const triggerCenter = $triggerPosition.left + $triggerPosition.width / 2;
      const tooltipLeft = $tooltipPosition.left;
      return css`
        left: ${triggerCenter - tooltipLeft}px;
        transform: translateX(-50%);
      `;
    }
  }};

  ${({ $placement }) => {
    if ($placement.includes('left')) {
      return css`
        left: calc(100% - 1px);
      `;
    }
    if ($placement.includes('right')) {
      return css`
        right: 100%;
        transform: rotate(180deg) translateY(50%);
      `;
    }
    if ($placement.includes('bottom')) {
      return css`
        bottom: 100%;
      `;
    }
    if ($placement.includes('top')) {
      return css`
        top: 100%;
        transform: rotate(180deg) translateX(50%);
      `;
    }
  }};
`;
